import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

export default function VirtualPostJob() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cats, setCats] = useState({ categories: [], service_types: [] });
  const [form, setForm] = useState({
    category: "",
    service_type: "",
    title: "",
    description: "",
    deliverables: "",
    deadline: "",
    budget_min: "",
    budget_max: "",
    pricing_type: "fixed",
    skills: "",
    preferred_country: "",
    preferred_language: "English",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) navigate("/login?redirect=/virtual/jobs/new");
    api.get("/virtual/categories").then((r) => setCats(r.data)).catch(() => {});
  }, [user, navigate]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description || !form.category || !form.service_type) {
      toast.error("Please fill title, description, category and service type");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        budget_min: parseFloat(form.budget_min) || 0,
        budget_max: parseFloat(form.budget_max) || parseFloat(form.budget_min) || 0,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.post("/virtual/jobs", payload);
      toast.success("Project posted! Freelancers can now apply.");
      navigate(`/virtual/jobs/${data.job_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post project");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <section className="pt-24 pb-16 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/virtual" className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 hover:text-slate-900">← Hire Virtual Staff</Link>
        <h1 className="font-display text-4xl mt-2 mb-2">Post a virtual project</h1>
        <p className="text-sm text-slate-600 mb-8">Tell us what you need and qualified freelancers will apply within hours.</p>

        <form onSubmit={submit} className="space-y-5" data-testid="virtual-job-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Category *</span>
              <select value={form.category} onChange={upd("category")} required className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vjob-category">
                <option value="">Select…</option>
                {cats.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Service type *</span>
              <select value={form.service_type} onChange={upd("service_type")} required className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vjob-service">
                <option value="">Select…</option>
                {cats.service_types.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Project title *</span>
            <input value={form.title} onChange={upd("title")} required maxLength={120} placeholder="e.g. Build a Shopify store for skincare brand" className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vjob-title" />
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Description *</span>
            <textarea value={form.description} onChange={upd("description")} required rows={6} placeholder="What needs to be done? Goals, current state, target audience…" className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vjob-description" />
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Deliverables</span>
            <textarea value={form.deliverables} onChange={upd("deliverables")} rows={3} placeholder="e.g. Live store with 5 products, payment gateway, mobile-responsive" className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vjob-deliverables" />
          </label>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Pricing *</span>
              <select value={form.pricing_type} onChange={upd("pricing_type")} className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vjob-pricing">
                <option value="fixed">Fixed price</option>
                <option value="hourly">Hourly</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Budget min (CAD) *</span>
              <input type="number" min="0" value={form.budget_min} onChange={upd("budget_min")} required placeholder="500" className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vjob-budget-min" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Budget max (CAD)</span>
              <input type="number" min="0" value={form.budget_max} onChange={upd("budget_max")} placeholder="1500" className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vjob-budget-max" />
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Deadline</span>
              <input type="date" value={form.deadline} onChange={upd("deadline")} className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vjob-deadline" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Preferred country</span>
              <input value={form.preferred_country} onChange={upd("preferred_country")} placeholder="Any" className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vjob-country" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Preferred language</span>
              <input value={form.preferred_language} onChange={upd("preferred_language")} placeholder="English" className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vjob-language" />
            </label>
          </div>

          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Required skills (comma-separated)</span>
            <input value={form.skills} onChange={upd("skills")} placeholder="Shopify, Liquid, JavaScript" className="mt-1 w-full px-3 py-3 border border-slate-300" data-testid="vjob-skills" />
          </label>

          <div className="flex items-center justify-between pt-4 border-t border-slate-200">
            <Link to="/virtual" className="text-sm text-slate-500 hover:text-slate-900">Cancel</Link>
            <button type="submit" disabled={saving} className="bg-brand text-white px-6 py-3 font-semibold hover:bg-[#122148] disabled:opacity-50" data-testid="vjob-submit">
              {saving ? "Posting…" : "Post project"}
            </button>
          </div>
        </form>
      </section>
      <Footer />
    </div>
  );
}
